// ============================================================================
// Line Official Account — user profile lookup.
//
// `GET /v2/bot/profile/{userId}` returns the public profile for a user who
// has added the OA as a friend. For users who haven't added the OA, Line
// returns 404 — we surface that as `USER_UNAVAILABLE` via the classifier.
//
// Docs: https://developers.line.biz/en/reference/messaging-api/#get-profile
// ============================================================================

import type { FetchUserProfileArgs, UserProfile } from '../../capabilities/messaging'
import {
  classifyLineError,
  LineOaError,
  parseRetryAfterMs,
  type LineErrorEnvelope,
  type LineUserProfileRaw,
} from './types'

const LINE_API_BASE = 'https://api.line.me'

// ============================================================================
// getLineUserProfile
// ============================================================================

export async function getLineUserProfile(
  userIdOrArgs: string | FetchUserProfileArgs,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<UserProfile> {
  const userId =
    typeof userIdOrArgs === 'string' ? userIdOrArgs : userIdOrArgs?.userId
  validateUserId(userId)
  assertAccessToken(accessToken)

  // Bearer auth + path param — token never appears in the URL.
  // `encodeURIComponent` blocks any path-traversal tricks; Line userIds are
  // `U` + 32 hex so encoding is effectively a no-op in practice, but the
  // belt-and-braces check costs nothing.
  const url = `${LINE_API_BASE}/v2/bot/profile/${encodeURIComponent(userId!)}`

  const response = await fetchImpl(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  const raw = (await response.json()) as LineUserProfileRaw
  return mapProfile(userId!, raw)
}

// ============================================================================
// Mapping — Line profile → capability `UserProfile`
// ============================================================================

function mapProfile(userId: string, raw: LineUserProfileRaw): UserProfile {
  const displayName =
    typeof raw?.displayName === 'string' && raw.displayName.trim().length > 0
      ? raw.displayName.trim()
      : userId

  const profile: UserProfile = {
    id: raw?.userId && typeof raw.userId === 'string' ? raw.userId : userId,
    name: displayName,
    avatarUrl: typeof raw?.pictureUrl === 'string' ? raw.pictureUrl : undefined,
    locale: typeof raw?.language === 'string' ? raw.language : undefined,
    extra: {
      statusMessage:
        typeof raw?.statusMessage === 'string' ? raw.statusMessage : undefined,
    },
  }
  return profile
}

// ============================================================================
// Internal helpers
// ============================================================================

async function errorFromResponse(response: Response): Promise<LineOaError> {
  let envelope: LineErrorEnvelope | null = null
  try {
    envelope = (await response.json()) as LineErrorEnvelope
  } catch {
    // Non-JSON body — leave envelope null.
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  return classifyLineError(envelope, response.status, retryAfterMs)
}

function validateUserId(userId: string | undefined | null): void {
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    throw new LineOaError('INVALID_INPUT', 'getLineUserProfile: userId is required')
  }
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new LineOaError('INVALID_INPUT', 'accessToken is required')
  }
}
